import axios, { AxiosError, AxiosResponse } from "axios";

export const api = axios.create({ baseURL: "/.netlify/functions/" });

export interface ApiError<P = unknown> {
	response: {
		status: number;
		data: ApiErrorData<P>;
	};
}

interface ApiErrorData<P = unknown> {
	code: string;
	payload: P;
}

export function isAxiosError(error: unknown): error is AxiosError {
	return isObject(error) && "response" in error;
}

export function isApiError<P = unknown>(error: unknown): error is ApiError<P> {
	return (
		isObject(error) &&
		isObject(error.response) &&
		isObject(error.response.data) &&
		"code" in error.response.data &&
		"payload" in error.response.data
	);
}

export function isApiErrorWithCode<P = unknown>(
	error: unknown,
	code: string
): error is ApiError<P> {
	return isApiError(error) && error.response.data.code === code;
}

export function getApiErrorData<P = unknown>({
	response: { data }
}: ApiError<P>): ApiErrorData<P> {
	return data;
}

function isObject(value: any): value is { [key: string]: unknown } {
	return typeof value === "object" && value !== null;
}

export async function fetchWithRetries<T>(
	url: string,
	step: number
): Promise<AxiosResponse<T>> {
	try {
		return await api.get<T>(url);
	} catch (error) {
		if (step < 5) {
			await new Promise(resolve => {
				window.setTimeout(resolve, 500);
			});
			return fetchWithRetries(url, step + 1);
		} else {
			throw error;
		}
	}
}
